//Libs
import React, { useCallback, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  CellClickedEvent,
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import AddCircleIcon from "@mui/icons-material/AddCircle";

//Local
import { ExistingProject } from "../../models/project";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import SearchBox from "../../components/SearchBox";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import HeadingOne from "../../components/HeadingOne";
import { useStiltNav } from "../../navigation";

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import "../../assets/css/ag-grid-inject-height.css";

interface Props {
  projects: ExistingProject[];
  userDisplayNamesMap: Record<string, string>;
  goToProjectDetailsPage: (projectID: string) => void;
}

export default function ProjectsListPage({
  projects,
  userDisplayNamesMap,
  goToProjectDetailsPage,
}: Props) {
  const stiltNav = useStiltNav();
  const [gridReady, setGridReady] = useState(false);
  const gridRef = React.useRef<any>(null);

  const renderTitle = (params: ICellRendererParams): JSX.Element => {
    return (
      <div className="flex h-full items-center">
        <span className="text-base font-medium">{params.value}</span>
      </div>
    );
  };

  const renderDescription = (params: ICellRendererParams): JSX.Element => {
    return (
      <div className="flex h-full items-center">
        <span className="text-sm text-gray-600">
          {params.value || "No description provided"}
        </span>
      </div>
    );
  };

  const renderTimestamp = (params: ICellRendererParams): JSX.Element => {
    return (
      <div className="flex h-full items-center">
        {params.value != null ? convertToReadableTimestamp(params.value) : ""}
      </div>
    );
  };

  const renderUserName = useCallback(
    (params: ICellRendererParams): JSX.Element => {
      const displayName = userDisplayNamesMap[params.value] || params.value;
      return <div className="flex h-full items-center">{displayName}</div>;
    },
    [userDisplayNamesMap],
  );

  const renderJobCount = (params: ICellRendererParams): JSX.Element => {
    const jobCount = params.data.craftRecordIDs?.length || 0;
    return <div className="flex h-full items-center">{jobCount}</div>;
  };

  const renderStatus = (params: ICellRendererParams): JSX.Element => {
    const isOpen = params.data.open;
    return (
      <div className="flex h-full items-center">
        <div
          className={`rounded-full px-2.5 pb-1 pt-0.5 text-xs font-medium ${
            isOpen ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
          }`}
        >
          {isOpen ? "Open" : "Closed"}
        </div>
      </div>
    );
  };

  const columnDefs: ColDef[] = [
    {
      field: "title",
      headerName: "Project Name",
      cellRenderer: renderTitle,
      tooltipValueGetter: (params) => params.value,
      minWidth: 200,
      flex: 2,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
    },
    {
      field: "description",
      headerName: "Description",
      cellRenderer: renderDescription,
      tooltipValueGetter: (params) => params.value,
      minWidth: 250,
      flex: 3,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
    },
    {
      field: "craftRecordIDs",
      headerName: "Jobs",
      cellRenderer: renderJobCount,
      tooltipValueGetter: (params) =>
        `${params.data.craftRecordIDs?.length || 0} jobs`,
      maxWidth: 100,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
    },
    {
      field: "open",
      headerName: "Status",
      cellRenderer: renderStatus,
      tooltipValueGetter: (params) => (params.data.open ? "Open" : "Closed"),
      maxWidth: 120,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
    },
    {
      field: "createdBy",
      headerName: "Created By",
      cellRenderer: renderUserName,
      tooltipValueGetter: (params) =>
        userDisplayNamesMap[params.value] || params.value,
      getQuickFilterText: (params: GetQuickFilterTextParams) =>
        userDisplayNamesMap[params.data.createdBy] || params.data.createdBy,
      maxWidth: 150,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
    },
    {
      field: "timestampCreated",
      headerName: "Created Date",
      valueFormatter: (params: ValueFormatterParams) => params.value,
      cellRenderer: renderTimestamp,
      tooltipValueGetter: (params) =>
        convertToReadableTimestamp(params.data.timestampCreated),
      maxWidth: 180,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
    },
    {
      field: "timestampLastModified",
      headerName: "Last Modified",
      valueFormatter: (params: ValueFormatterParams) => params.value,
      cellRenderer: renderTimestamp,
      tooltipValueGetter: (params) =>
        convertToReadableTimestamp(params.data.timestampLastModified),
      maxWidth: 180,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
    },
  ];

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      resizable: true,
      filter: true,
      sortable: true,
      flex: 1,
    };
  }, []);

  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setQuickFilter(
      (document.getElementById("filter-text-box") as HTMLInputElement).value,
    );
  }, []);

  const onGridReady = useCallback(() => {
    setGridReady(true);
    gridRef.current?.api?.sizeColumnsToFit();
  }, []);

  const createNewProjectButton = (
    <BaseButtonPrimary
      type="button"
      onClick={() =>
        console.log("Create new project - functionality not implemented")
      }
      className="w-full text-primary sm:w-48"
    >
      <AddCircleIcon fontSize="small" className="mr-2" />
      {"New Project"}
    </BaseButtonPrimary>
  );

  return (
    <div className="p-4">
      <HeadingOne className="mb-4">Projects</HeadingOne>

      <div className="mb-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
        <SearchBox
          onInput={onFilterTextBoxChanged}
          widthClasses="w-full sm:w-96"
        />
        {createNewProjectButton}
      </div>

      <div className="ag-theme-alpine h-[800px] max-h-[68vh]">
        <AgGridReact
          ref={gridRef}
          defaultColDef={defaultColDef}
          rowData={projects}
          animateRows={true}
          rowSelection="single"
          cacheQuickFilter={true}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          rowHeight={48}
          onRowClicked={(event: CellClickedEvent) => {
            goToProjectDetailsPage(event.data.id);
          }}
        />
      </div>
    </div>
  );
}
